import { SurveySectionViewModel } from '../types';
import { GrowegyTextEditor } from '../../commons/components/GrowegyTextEditor';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setSectionText } from '../businessPlanSlice';
import { Button } from 'react-bootstrap';
import { MultilevelTabPanel } from '../../commons/components/MultilevelTabPanel';
import { SurveySectionInstruction } from './SurveySectionInstruction';
import { SurveySectionExamples } from './SurveySectionExamples';
import { TextAndIcon } from '../../commons/components/TextAndIcon';
import { AiStarsIcon } from '../../assets/icons';
import { SurveySectionChat } from './SurveySectionChat';
import React, { useCallback, useRef, useState } from 'react';
import {
  Chat,
  TOOL_CALL_USER_RESULT_OK,
  TOOL_CALL_USER_RESULT_REJECT,
  TOOL_UPDATE_BUSINESS_PLAN_SECTION,
  UpdateBusinessPlanSectionToolCall,
} from '../../growegy-ai/types';
import { PopupChatFunctions } from '../../commons/components/PopupChat';
import { useBusinessPlanMessageGenerator } from '../hooks/useBusinessPlanMessageGenerator';
import { useSendMessageMutation } from '../../growegy-ai/chat/hooks/useSendMessageMutation';
import { useChatContext } from '../../growegy-ai/chat/chatContext';
import { createNewChat, getTempMessageId } from '../../growegy-ai/util';
import { CHAT_TYPE_BUSINESS_PLAN } from '../../growegy-ai/dto';
import { stringToComplexContent } from '../../growegy-ai/chat/components/TextMessageInput';
import _ from 'lodash';
import { useMutation } from '@tanstack/react-query';
import { useNotification } from '../../notification/notificationsContext';
import { ANSWER_MESSAGE_STATUS_ERROR } from '../../growegy-ai/growegyAIApi';
import { ChatRealmContextProvider } from '../../growegy-ai/chat/chatRealmContext';
import { ItemDetailsProps } from '../../commons/components/ItemListAndDetails';
import { useSurveyWithLocalChanges } from '../hooks/useSurveyWithLocalChanges';
import { BoxSpinner } from '../../commons/components/Spinner';
import useAnalyticsPage from '../../web-analytics/hooks/useAnalyticsPage';
import { Page } from '../../web-analytics/Page';

export type SurveySectionActionPayload = {};

export const SurveySection = (
  props: ItemDetailsProps<SurveySectionViewModel, SurveySectionActionPayload>
) => {
  const { item } = props;
  useAnalyticsPage(Page.BUSINESS_PLAN_SECTION, { section: props.item.key });
  return (
    <ChatRealmContextProvider chatType={CHAT_TYPE_BUSINESS_PLAN} chatSubtype={item.key}>
      <SurveySectionInternal {...props} />
    </ChatRealmContextProvider>
  );
};

const SurveySectionInternal = (
  props: ItemDetailsProps<SurveySectionViewModel, SurveySectionActionPayload>
) => {
  const { item } = props;

  const draftValue = useAppSelector((state) => state.businessPlan.sections[item.key]) ?? item.value;
  const dispatch = useAppDispatch();
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const popupChatRef = useRef<PopupChatFunctions | null>(null);
  const { getCurrentModel } = useChatContext();

  const { notifyError } = useNotification();
  const messageGenerator = useBusinessPlanMessageGenerator();
  const { mutateAsync: sendMessageMutation } = useSendMessageMutation({
    onChatCreated: setSelectedChat,
    onChatCreationFailed: () =>
      notifyError({
        notificationMsg: 'Something went wrong while starting new chat.',
        logMsg: 'Something went wrong while starting new chat.',
      }),
    onMessageCreated: () => {},
    gptModel: getCurrentModel(),
    systemMessage: () =>
      messageGenerator(draftValue, item.key).chatGptGenerateTemplateSystemMessage,
    setComplexContent: () => {},
    libraryPromptWizardState: null,
    setLibraryPromptWizardState: () => {},
  });

  const { mutate: composeMutation, status: composeStatus } = useMutation({
    mutationFn: async () => {
      try {
        const { chatGptGenerateTemplatePrompt } = messageGenerator(draftValue, item.key);
        const { chat, chatPromptAndAnswer } = await sendMessageMutation({
          chat: createNewChat(CHAT_TYPE_BUSINESS_PLAN, item.key),
          contentToSend: stringToComplexContent(chatGptGenerateTemplatePrompt),
          source: 'business-plan-editor',
          messageClientId: getTempMessageId(),
        });
        if (
          !chatPromptAndAnswer?.answer?.toolCalls ||
          chatPromptAndAnswer.answerStatus === ANSWER_MESSAGE_STATUS_ERROR
        ) {
          popupChatRef.current?.expand();
          return;
        }
        if (chatPromptAndAnswer?.answer?.toolCalls) {
          const updateToolCall = _(chatPromptAndAnswer.answer.toolCalls).findLast(
            (x) => x.name === TOOL_UPDATE_BUSINESS_PLAN_SECTION
          ) as UpdateBusinessPlanSectionToolCall | undefined;
          for (const toolCall of chatPromptAndAnswer.answer.toolCalls) {
            if (updateToolCall?.id === toolCall.id) {
              await sendMessageMutation({
                chat,
                contentToSend: stringToComplexContent('Proceed'),
                source: 'business-plan-editor',
                promptExtensions: [
                  {
                    resolvedToolCall: {
                      id: updateToolCall.id,
                      name: updateToolCall.name,
                      arguments: {
                        section_text: updateToolCall.arguments.section_text,
                        section_key: item.key,
                      },
                    },
                    userResult: TOOL_CALL_USER_RESULT_OK,
                  },
                ],
                messageClientId: getTempMessageId(),
              });
            } else {
              await sendMessageMutation({
                chat,
                contentToSend: null,
                source: 'business-plan-editor',
                promptExtensions: [
                  {
                    toolCallId: toolCall.id,
                    toolCallName: toolCall.name,
                    userResult: TOOL_CALL_USER_RESULT_REJECT,
                  },
                ],
                messageClientId: getTempMessageId(),
              });
            }
          }
          if (!updateToolCall) {
            popupChatRef.current?.expand();
            return;
          }
        }
      } catch (e) {
        popupChatRef.current?.expand();
      }
    },
  });

  const onToolCallProceed = useCallback(() => popupChatRef.current?.collapse(), []);

  const {
    hasUnsavedChanges,
    saveSurveyMutation: { mutate: saveSurvey, status: saveSurveyStatus },
  } = useSurveyWithLocalChanges();

  if (!item) return <></>;

  return (
    <div className="survey-section__container">
      <div className="survey-section__content">
        <div className="survey-section__item-wrapper">
          <MultilevelTabPanel
            noHeaderPadding={true}
            tabs={[
              {
                title: 'Instruction',
                dataTest: 'survey-section__instruction',
                subTabs: [
                  {
                    id: 'instruction-tab',
                    title: null,
                    hasAccess: true,
                    dataTest: 'survey-section__instruction-subtab',
                    body: <SurveySectionInstruction instruction={item.metadata.instruction} />,
                  },
                ],
              },
              {
                title: 'Examples',
                dataTest: 'survey-section__examples',
                subTabs: [
                  {
                    id: 'instruction-tab',
                    title: null,
                    hasAccess: true,
                    dataTest: 'survey-section__examples-subtab',
                    body: <SurveySectionExamples examples={item.metadata.examples} />,
                  },
                ],
              },
            ]}
          />
        </div>
        <div className="survey-section__item-wrapper survey-section__editor">
          <div className="survey-section__input-container">
            <GrowegyTextEditor
              setValue={(value) => dispatch(setSectionText({ key: item.key, text: value }))}
              initialValue={draftValue}
              disabled={composeStatus === 'loading'}
              readOnly={false}
              isInvalid={false}
              dataTest="survey-section__value"
              className="survey-section__value-input"
              placeholder="Type your answer here or click 'Help me write' below…"
            />
            {composeStatus === 'loading' && <BoxSpinner />}
          </div>
          <div className="survey-section__toolbar">
            <Button
              type="button"
              className="button-apply"
              onClick={() => composeMutation()}
              disabled={composeStatus === 'loading'}
            >
              <TextAndIcon
                icon={<AiStarsIcon style={{ fill: 'white' }} />}
                text="Help me write"
                textClassName="survey-section__generate-button-text"
              />
            </Button>
            <div className="flex-grow-1" />
            <Button
              type="button"
              className="button-apply survey-section__save-button"
              disabled={!hasUnsavedChanges || saveSurveyStatus === 'loading'}
              onClick={() => saveSurvey()}
              data-test="confirm-code__resend"
            >
              {hasUnsavedChanges ? 'Save' : 'Saved'}
            </Button>
            <SurveySectionChat
              ref={popupChatRef}
              selectedChat={selectedChat}
              systemMessage={() =>
                messageGenerator(draftValue, item.key).chatGptGenerateTemplateSystemMessage
              }
              onToolCallProceed={onToolCallProceed}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
